import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, clearStoredToken } from "@/lib/api";
import { registerPushNotifications, unregisterPushNotifications } from "@/lib/pushNotifications";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Fire-and-forget: on a native shell, ask the OS for a push token and
      // register it with our backend so this device can receive notifications.
      // No-op on the web build.
      registerPushNotifications().catch(() => {});
    } catch {
      // If we had a stored JWT but /auth/me rejected it, the token is stale
      // (expired, server-side rotated, or bogus). Wipe it so we don't keep
      // sending an invalid Bearer header on every subsequent request.
      clearStoredToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check; AuthCallback will handle it.
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  // Global session-expired handler: when any API call gets 401 (after the
  // user has already been authenticated), the api.js interceptor fires a
  // "voxly:session-expired" event. We clear local state, tell the user, and
  // route them back to the login screen so they can re-auth cleanly.
  useEffect(() => {
    const onExpired = () => {
      // Only react if we currently believe the user is signed in. Otherwise
      // this is just a regular "not logged in yet" 401 on a public page.
      setUser((prev) => {
        if (!prev) return prev;
        toast.error("Your session expired. Please sign in again.");
        // Soft redirect via location so we wipe SPA state cleanly.
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth") && window.location.pathname !== "/") {
          setTimeout(() => { window.location.href = "/"; }, 400);
        }
        return null;
      });
    };
    window.addEventListener("voxly:session-expired", onExpired);
    return () => window.removeEventListener("voxly:session-expired", onExpired);
  }, []);

  const logout = useCallback(async () => {
    // Unregister the device's push token first so the next user on this device
    // doesn't inherit our notifications. No-op on the web build.
    try { await unregisterPushNotifications(); } catch { /* noop */ }
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network/401 on logout is non-fatal — we still clear local state.
      if (process.env.NODE_ENV !== "production") {
        console.warn("[auth] logout request failed (clearing client state anyway)", err);
      }
    }
    clearStoredToken();
    setUser(null);
  }, []);

  // Memoize the context value so consumers (every page using useAuth) don't
  // re-render whenever an unrelated piece of AuthProvider state changes.
  const value = useMemo(() => ({ user, setUser, loading, refresh, logout }), [user, loading, refresh, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
